import { IFontFamily } from '@/interfaces/editor'
import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import api from '@services/api'
import { AxiosError } from 'axios'
import { MediaFontRepository } from '@/scenes/engine/objects/media-repository/media_font_repository'
import {
  getUserLanguages,
  filterFontsByLocale,
  buildFontGroups
} from './utils'
import { FontGroup, FontPack } from './types'

export const setFonts = createAction<IFontFamily[]>('fonts/setFonts')
export const setFontPacks = createAction<IFontFamily[]>('fonts/setFontPacks')
export const setFontGroups = createAction<FontGroup>('fonts/setFontGroups')
export const setFontsLoading = createAction<boolean>('fonts/setFontsLoading')

export const getFonts = createAsyncThunk<void, never, { rejectValue: Record<string, string[]> }>(
  'fonts/getFonts',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const fonts = await api.getFonts()

      dispatch(setFonts(fonts))
    } catch (err) {
      return rejectWithValue(((err as AxiosError).response?.data as any)?.error.data || null)
    }
  }
)

export const getFontPacks = createAsyncThunk<
  void,
  { userLanguages?: readonly string[] } | void,
  { rejectValue: Record<string, string[]> }
>(
  'fonts/getFontPacks',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const userLanguages = params && 'userLanguages' in params ? params.userLanguages : getUserLanguages()
      const fontPacks = await api.getFontPacks() as FontPack[]
      const filteredFontPacks = filterFontsByLocale(fontPacks, userLanguages)
      dispatch(setFontPacks(filteredFontPacks))
    } catch (err) {
      return rejectWithValue(((err as AxiosError).response?.data as any)?.error.data || null)
    }
  }
)

export const loadFontsWithGroups = createAsyncThunk<
  void,
  { userLanguages?: readonly string[] } | void,
  { rejectValue: Record<string, string[]> }
>(
  'fonts/loadFontsWithGroups',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setFontsLoading(true))
      
      const userLanguages = params && 'userLanguages' in params ? params.userLanguages : getUserLanguages()
      
      // Load and filter font packs
      const fontPacks = await api.getFontPacks() as FontPack[]
      const filteredFontPacks = filterFontsByLocale(fontPacks, userLanguages)
      dispatch(setFontPacks(filteredFontPacks))

      // Get all fonts from API
      const allFonts = await api.getFonts()
      dispatch(setFonts(allFonts))
      
      // Build font groups using shared helper
      const fontGroups = buildFontGroups(allFonts, filteredFontPacks)

      dispatch(setFontGroups(fontGroups))
      
      dispatch(setFontsLoading(false))
    } catch (err) {
      dispatch(setFontsLoading(false))
      return rejectWithValue(((err as AxiosError).response?.data as any)?.error.data || null)
    }
  }
)

export const loadFont = createAsyncThunk<
  any,
  { family: string; url: string; options: { style: string; weight: string } },
  { rejectValue: string }
>(
  'fonts/loadFont',
  async ({ family, url, options }, { rejectWithValue }) => {
    try {
      const fontInfo = await MediaFontRepository.getInstance().loadFontFromUrl(
        family,
        url,
        options
      )
      return fontInfo
    } catch (error) {
      console.error(`Failed to load font ${family}:`, error)
      return rejectWithValue(`Failed to load font ${family}`)
    }
  }
)

export const preloadFonts = createAsyncThunk<
  void,
  { fontFamilies: IFontFamily[]; concurrency?: number },
  { rejectValue: string }
>(
  'fonts/preloadFonts',
  async ({ fontFamilies, concurrency = 3 }, { rejectWithValue }) => {
    try {
      const fontRepository = MediaFontRepository.getInstance()
      
      for (let i = 0; i < fontFamilies.length; i += concurrency) {
        const batch = fontFamilies.slice(i, i + concurrency)
        const batchPromises = batch.map(font => {
          const fontFile = font.files['regular' as any] as unknown as string
          if (!fontFile) return Promise.resolve()
          
          // Use lightweight preload path - only load FontFace, skip parsing
          // This reduces memory usage during hover/scroll font previews
          return fontRepository.loadFontFromUrl(
            font.family,
            fontFile,
            { style: 'normal', weight: '400' } 
          ).catch(err => {
            console.warn(`Failed to preload font ${font.family}:`, err)
          })
        })
        await Promise.allSettled(batchPromises)
      }
    } catch (error) {
      return rejectWithValue('Failed to preload fonts')
    }
  }
)
